/**
 * Planning Studio (M27H)
 * =========================
 * The CFO-facing UI for the M27 planning engine. Six tabs:
 *
 *   1. Scenarios     -- list, state badges, transition controls, snapshots
 *   2. Drivers       -- formula registry, required assumptions
 *   3. BvA           -- variance grid + variance commentary (Claude)
 *   4. Ask           -- conversational Q&A over the plan (read + propose)
 *   5. Drafts        -- staged Claude edit batches w/ promote/discard
 *   6. Dimensional   -- workforce / capex / projects / sales / tax / LRP
 *
 * Designed to be the single page a CFO uses for plan + actuals. All
 * data comes from /api/v1/planning/*.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { API_V1, authFetch, formatAmount } from '../constants';
import PageLayout from '../components/ui/PageLayout';
import TabBar from '../components/ui/TabBar';
import GuidedBuilder from './planning/GuidedBuilder';
import StatementView from './planning/StatementView';

const API = `${API_V1}/planning`;

const STATE_COLORS: Record<string, { bg: string; fg: string; border: string }> = {
  DRAFT:    { bg: '#f1f5f9', fg: '#475569', border: '#cbd5e1' },
  PROPOSED: { bg: '#fef3c7', fg: '#a16207', border: '#fbbf24' },
  APPROVED: { bg: '#dbeafe', fg: '#1d4ed8', border: '#60a5fa' },
  ACTIVE:   { bg: '#d1fae5', fg: '#047857', border: '#34d399' },
  ARCHIVED: { bg: '#e5e7eb', fg: '#6b7280', border: '#9ca3af' },
};

const SOURCE_COLORS: Record<string, string> = {
  HUMAN:  '#1e3a5f',
  CLAUDE: '#7c3aed',
  AUTO:   '#0e7490',
};

const thStyle: React.CSSProperties = { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '10px 14px', color: 'var(--text-hint)', textAlign: 'left' };
const tdStyle: React.CSSProperties = { fontSize: 13, padding: '10px 14px', borderTop: '1px solid var(--border-subtle)' };

const PANEL: React.CSSProperties = {
  background: 'var(--surface-default, #fff)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 6,
  padding: 16,
  marginBottom: 16,
};

const SECTION_TITLE: React.CSSProperties = {
  fontSize: 13, fontWeight: 700, color: 'var(--navy-900)',
  marginBottom: 12, letterSpacing: 0.2,
};

interface Scenario {
  id: number;
  name: string;
  scenario_type: string;
  fiscal_year: number | null;
  is_active: boolean;
  has_computed_outputs: boolean;
  created_at: string | null;
  status?: string;
  approved_by?: string | null;
  approved_at?: string | null;
}

interface PlanTool {
  name: string;
  description: string;
}

interface AuditEdit {
  id: number;
  scenario_id: number;
  source: string;
  action: string;
  entity_type: string;
  field_name: string | null;
  before_value: any;
  after_value: any;
  actor: string | null;
  created_at: string | null;
  diff_summary?: string | null;
}

interface Draft {
  id: number;
  scenario_id: number | null;
  batch_id: string;
  action: string;
  entity_type: string;
  field: string | null;
  before: any;
  after: any;
  rationale: string | null;
  status: string;
  actor: string | null;
  created_at: string | null;
}

interface BvARow {
  metric: string;
  actuals: number;
  plan: number;
  variance: number;
  var_pct: number | null;
  forecast: number;
  fvb: number;
  fvb_pct: number | null;
}

const TABS = [
  { key: 'now', label: 'Now' },
  { key: 'statement', label: 'Statement' },
  { key: 'lines', label: 'Lines' },
  { key: 'bva', label: 'BvA' },
  { key: 'ask', label: 'Ask' },
  { key: 'drafts', label: 'Drafts' },
  { key: 'scenarios', label: 'Scenarios' },
  { key: 'dimensional', label: 'Dimensional' },
  { key: 'drivers', label: 'Drivers' },
];

const SOURCE_BAND_COLORS: Record<string, string> = {
  HUMAN:   '#1e3a5f', // navy -- entered by operator
  AI_SEED: '#0d9488', // teal -- extrapolated from actuals
  IMPORT:  '#7c3aed', // purple -- uploaded doc
  CLAUDE:  '#8b5cf6', // violet -- conversational AI
  DRIVER:  '#f59e0b', // amber -- formula-derived
  AUTO:    '#6b7280', // gray -- other automation
};

export default function PlanningStudio() {
  const [tab, setTab] = useState('now');
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  const showToast = useCallback((msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const loadScenarios = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/scenarios`);
      if (res.ok) {
        const data = await res.json();
        setScenarios(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      console.error('loadScenarios', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadScenarios(); }, [loadScenarios]);

  return (
    <PageLayout
      title="Planning Studio"
      subtitle="Driver-based planning, BvA, agentic editing, scenario snapshots, dimensional plans — backed by the M27 planning engine."
    >
      <TabBar tabs={TABS} active={tab} onChange={setTab} />

      {tab === 'now' && (
        <NowTab
          scenarios={scenarios}
          loading={loading}
          reload={loadScenarios}
          toast={showToast}
          goToTab={setTab}
        />
      )}
      {tab === 'scenarios' && (
        <ScenariosTab
          scenarios={scenarios}
          loading={loading}
          reload={loadScenarios}
          toast={showToast}
        />
      )}
      {tab === 'statement' && <StatementView scenarios={scenarios} toast={showToast} />}
      {tab === 'lines' && <LinesTab scenarios={scenarios} toast={showToast} />}
      {tab === 'drivers' && <DriversTab />}
      {tab === 'bva' && <BvATab scenarios={scenarios} />}
      {tab === 'ask' && <AskTab scenarios={scenarios} />}
      {tab === 'drafts' && <DraftsTab toast={showToast} />}
      {tab === 'dimensional' && <DimensionalTab scenarios={scenarios} />}

      {toast && (
        <div style={{
          position: 'fixed', bottom: 20, right: 20, padding: '10px 16px',
          borderRadius: 6, fontSize: 13, fontWeight: 600,
          background: toast.ok ? '#d1fae5' : '#fee2e2',
          color: toast.ok ? '#047857' : '#991b1b',
          border: `1px solid ${toast.ok ? '#34d399' : '#fca5a5'}`,
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        }}>
          {toast.msg}
        </div>
      )}
    </PageLayout>
  );
}

// ── Scenarios tab ────────────────────────────────────────────────────

function ScenariosTab({
  scenarios, loading, reload, toast,
}: {
  scenarios: Scenario[];
  loading: boolean;
  reload: () => void;
  toast: (msg: string, ok?: boolean) => void;
}) {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [snapshots, setSnapshots] = useState<any[]>([]);
  const [editsForSelected, setEditsForSelected] = useState<AuditEdit[]>([]);
  const [computing, setComputing] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-select first scenario so the right pane shows real data
  // instead of an empty placeholder (no-click test).
  useEffect(() => {
    if (selectedId == null && scenarios.length > 0) {
      setSelectedId(scenarios[0].id);
    }
  }, [scenarios, selectedId]);

  const selected = useMemo(
    () => scenarios.find(s => s.id === selectedId) || null,
    [scenarios, selectedId],
  );

  useEffect(() => {
    if (!selectedId) {
      setSnapshots([]); setEditsForSelected([]);
      return;
    }
    (async () => {
      try {
        const [sRes, eRes] = await Promise.all([
          authFetch(`${API}/scenarios/${selectedId}/snapshots?limit=30`),
          authFetch(`${API}/audit-log?scenario_id=${selectedId}&limit=20`),
        ]);
        if (sRes.ok) {
          const d = await sRes.json();
          setSnapshots(d.snapshots || []);
        }
        if (eRes.ok) {
          const d = await eRes.json();
          setEditsForSelected(Array.isArray(d) ? d : []);
        }
      } catch (e) { console.error(e); }
    })();
  }, [selectedId]);

  const transition = async (action: 'propose' | 'approve' | 'reject' | 'activate' | 'archive') => {
    if (!selectedId) return;
    try {
      const res = await authFetch(`${API}/scenarios/${selectedId}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ actor: 'OPERATOR', notes: '' }),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        toast(`${action.toUpperCase()}: ${data.from} → ${data.to}`);
        reload();
      } else {
        toast(`${action} failed: ${data.detail || data.reason || 'error'}`, false);
      }
    } catch (e: any) {
      toast(`${action} threw: ${e.message}`, false);
    }
  };

  const compute = async () => {
    if (!selectedId) return;
    setComputing(true);
    try {
      const res = await authFetch(`${API}/scenarios/${selectedId}/compute`, { method: 'POST' });
      if (res.ok) {
        toast('Compute complete');
        reload();
      } else {
        const d = await res.json().catch(() => ({}));
        toast(`Compute failed: ${d.detail || res.status}`, false);
      }
    } finally {
      setComputing(false);
    }
  };

  const onPickFile = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const uploadDocument = async (file: File) => {
    if (!selectedId) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('actor', 'OPERATOR');
      const res = await authFetch(`${API}/scenarios/${selectedId}/upload-document`, {
        method: 'POST',
        body: fd,
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.ok) {
        const staged = d.staged_count ?? 0;
        const rejected = d.rejected_count ?? 0;
        toast(`Staged ${staged} rows from ${file.name}${rejected ? ` (${rejected} rejected)` : ''}. Open Drafts to review.`);
        reload();
      } else {
        toast(`Upload failed: ${d.error || d.detail || res.status}`, false);
      }
    } catch (e: any) {
      toast(`Upload error: ${e.message}`, false);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const seedFromActuals = async () => {
    if (!selectedId) return;
    setSeeding(true);
    try {
      const res = await authFetch(`${API}/scenarios/${selectedId}/seed-from-actuals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lookback_months: 12, horizon_months: 12, actor: 'OPERATOR',
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.ok) {
        const written = d.lines_written ?? 0;
        const accts = d.accounts_seeded ?? 0;
        const methods = d.method_counts ? Object.entries(d.method_counts).map(([m, n]) => `${m}:${n}`).join(', ') : '';
        toast(`Seeded ${written} lines across ${accts} accounts${methods ? ' (' + methods + ')' : ''}`);
        reload();
      } else {
        toast(`Seed failed: ${d.error || d.detail || res.status}`, false);
      }
    } catch (e: any) {
      toast(`Seed error: ${e.message}`, false);
    } finally {
      setSeeding(false);
    }
  };

  const snapshot = async () => {
    if (!selectedId) return;
    const res = await authFetch(`${API}/scenarios/${selectedId}/snapshot`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ actor: 'OPERATOR', notes: 'Manual pin' }),
    });
    if (res.ok) {
      toast('Snapshot pinned');
      // reload snapshots
      setSelectedId(prev => prev); // no-op trigger
      const r = await authFetch(`${API}/scenarios/${selectedId}/snapshots?limit=30`);
      if (r.ok) {
        const d = await r.json();
        setSnapshots(d.snapshots || []);
      }
    } else {
      toast('Snapshot failed', false);
    }
  };

  const [showCreate, setShowCreate] = useState(false);

  if (loading) return <div style={{ padding: 24, color: 'var(--text-hint)' }}>Loading scenarios…</div>;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: 16 }}>
      {/* Scenario list */}
      <div style={PANEL}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={SECTION_TITLE}>Scenarios ({scenarios.length})</div>
          <SmallBtn primary onClick={() => setShowCreate(true)}>+ New</SmallBtn>
        </div>
        <div style={{ maxHeight: 600, overflow: 'auto' }}>
          {scenarios.map(s => (
            <div
              key={s.id}
              onClick={() => setSelectedId(s.id)}
              style={{
                padding: '10px 12px', borderRadius: 4,
                cursor: 'pointer',
                background: selectedId === s.id ? 'var(--accent-subtle, #f1f5f9)' : 'transparent',
                marginBottom: 6,
                border: selectedId === s.id ? '1px solid var(--border-default)' : '1px solid transparent',
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--navy-900)' }}>{s.name}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                <StateBadge status={s.status || (s.is_active ? 'ACTIVE' : 'ARCHIVED')} />
                <span style={{ fontSize: 11, color: 'var(--text-hint)' }}>
                  {s.scenario_type} · FY{s.fiscal_year}
                </span>
              </div>
            </div>
          ))}
          {scenarios.length === 0 && (
            <div style={{ padding: 12, fontSize: 12, color: 'var(--text-hint)' }}>
              No scenarios yet.
            </div>
          )}
        </div>
      </div>

      {/* Detail */}
      <div>
        {selected ? (
          <>
            <div style={PANEL}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: 'var(--navy-900)' }}>
                    {selected.name}
                  </h3>
                  <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center' }}>
                    <StateBadge status={selected.status || (selected.is_active ? 'ACTIVE' : 'ARCHIVED')} />
                    <span style={{ fontSize: 12, color: 'var(--text-hint)' }}>
                      {selected.scenario_type} · FY{selected.fiscal_year} · ID {selected.id}
                    </span>
                  </div>
                  {selected.approved_by && (
                    <div style={{ fontSize: 11, color: 'var(--text-hint)', marginTop: 4 }}>
                      Approved by {selected.approved_by} at {selected.approved_at}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <SmallBtn onClick={() => transition('propose')}>Propose</SmallBtn>
                  <SmallBtn onClick={() => transition('approve')}>Approve</SmallBtn>
                  <SmallBtn onClick={() => transition('reject')}>Reject</SmallBtn>
                  <SmallBtn onClick={() => transition('activate')}>Activate</SmallBtn>
                  <SmallBtn onClick={() => transition('archive')}>Archive</SmallBtn>
                </div>
              </div>
              <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <SmallBtn primary onClick={seedFromActuals} disabled={seeding}>
                  {seeding ? 'Seeding…' : 'Seed from actuals'}
                </SmallBtn>
                <SmallBtn onClick={onPickFile} disabled={uploading}>
                  {uploading ? 'Uploading…' : 'Upload document'}
                </SmallBtn>
                <SmallBtn onClick={compute} disabled={computing}>
                  {computing ? 'Computing…' : 'Compute scenario'}
                </SmallBtn>
                <SmallBtn onClick={snapshot}>Snapshot now</SmallBtn>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls,.pdf"
                  title="Upload budget / payroll / contract document"
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (f) uploadDocument(f);
                  }}
                  style={{ display: 'none' }}
                />
              </div>
            </div>

            <div style={PANEL}>
              <div style={SECTION_TITLE}>Snapshots ({snapshots.length})</div>
              {snapshots.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--text-hint)' }}>No snapshots yet.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--surface-muted, #f8fafc)' }}>
                      <th style={thStyle}>Kind</th>
                      <th style={thStyle}>Status</th>
                      <th style={thStyle}>Actor</th>
                      <th style={thStyle}>Notes</th>
                      <th style={thStyle}>At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {snapshots.map(s => (
                      <tr key={s.id}>
                        <td style={tdStyle}>
                          <span style={{ fontWeight: 600 }}>{s.kind}</span>
                        </td>
                        <td style={tdStyle}>{s.status || '—'}</td>
                        <td style={tdStyle}>{s.actor || '—'}</td>
                        <td style={tdStyle} title={s.notes}>{(s.notes || '').slice(0, 60)}</td>
                        <td style={tdStyle}>{shortDate(s.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <GuidedBuilder
              scenarioId={selected.id}
              scenarioName={selected.name}
              toast={toast}
              onChanged={reload}
            />

            <div style={PANEL}>
              <div style={SECTION_TITLE}>Recent edits ({editsForSelected.length})</div>
              {editsForSelected.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--text-hint)' }}>No recent edits.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--surface-muted, #f8fafc)' }}>
                      <th style={thStyle}>Source</th>
                      <th style={thStyle}>Action</th>
                      <th style={thStyle}>Field</th>
                      <th style={thStyle}>Before → After</th>
                      <th style={thStyle}>Actor</th>
                      <th style={thStyle}>At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {editsForSelected.map(e => (
                      <tr key={e.id}>
                        <td style={tdStyle}>
                          <SourceBadge source={e.source} />
                        </td>
                        <td style={tdStyle}>{e.action}</td>
                        <td style={tdStyle}>{e.field_name || '—'}</td>
                        <td style={tdStyle}>
                          <span style={{ color: 'var(--text-hint)' }}>{fmtVal(e.before_value)}</span>
                          {' → '}
                          <span style={{ fontWeight: 600 }}>{fmtVal(e.after_value)}</span>
                        </td>
                        <td style={tdStyle}>{e.actor || '—'}</td>
                        <td style={tdStyle}>{shortDate(e.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        ) : (
          <div style={{ ...PANEL, color: 'var(--text-hint)' }}>
            Select a scenario from the left to inspect.
          </div>
        )}
      </div>

      {showCreate && (
        <NewScenarioModal
          onClose={() => setShowCreate(false)}
          onCreated={(newId) => {
            setShowCreate(false);
            setSelectedId(newId);
            reload();
          }}
          toast={toast}
        />
      )}
    </div>
  );
}

// ── Drivers tab ────────────────────────────────────────────────────

function DriversTab() {
  const [formulas, setFormulas] = useState<any[]>([]);
  const [order, setOrder] = useState<string[]>([]);
  const [required, setRequired] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(`${API}/formulas`);
        if (res.ok) {
          const d = await res.json();
          setFormulas(d.formulas || []);
          setOrder(d.evaluation_order || []);
          setRequired(d.required_assumptions || []);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div style={{ padding: 24 }}>Loading driver graph…</div>;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
        <StatCard label="Formulas" value={formulas.length} />
        <StatCard label="Topo order length" value={order.length} />
        <StatCard label="Required assumptions" value={required.length} />
      </div>

      <div style={PANEL}>
        <div style={SECTION_TITLE}>Formula registry</div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--surface-muted, #f8fafc)' }}>
              <th style={thStyle}>ID</th>
              <th style={thStyle}>Output</th>
              <th style={thStyle}>Category</th>
              <th style={thStyle}>Inputs</th>
              <th style={thStyle}>Expression</th>
            </tr>
          </thead>
          <tbody>
            {formulas.map((f: any) => (
              <tr key={f.id}>
                <td style={tdStyle}>
                  <code style={{ fontSize: 12 }}>{f.id}</code>
                </td>
                <td style={{ ...tdStyle, fontWeight: 600 }}>{f.output}</td>
                <td style={tdStyle}>{f.category}</td>
                <td style={{ ...tdStyle, fontSize: 11 }}>
                  {(f.inputs || []).join(', ')}
                </td>
                <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 11 }}>
                  {f.expression}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={PANEL}>
        <div style={SECTION_TITLE}>Required assumptions</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {required.map(a => (
            <span key={a} style={{
              padding: '4px 8px', borderRadius: 4, fontSize: 11,
              background: 'var(--accent-subtle, #f1f5f9)', color: 'var(--navy-900)',
              border: '1px solid var(--border-subtle)',
            }}>{a}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── BvA tab ──────────────────────────────────────────────────────────

function BvATab({ scenarios }: { scenarios: Scenario[] }) {
  const [scenarioId, setScenarioId] = useState<number | null>(null);
  const [period, setPeriod] = useState('');
  const [grid, setGrid] = useState<BvARow[]>([]);
  const [narrative, setNarrative] = useState<string | null>(null);
  const [loadingGrid, setLoadingGrid] = useState(false);
  const [loadingNarrative, setLoadingNarrative] = useState(false);

  useEffect(() => {
    if (scenarios.length && !scenarioId) {
      setScenarioId(scenarios[0].id);
    }
  }, [scenarios, scenarioId]);

  const load = async () => {
    if (!scenarioId || !period) return;
    setLoadingGrid(true);
    try {
      const res = await authFetch(`${API}/bva?period=${period}&scenario_id=${scenarioId}`);
      const d = await res.json();
      setGrid((d.rows || []) as BvARow[]);
    } finally {
      setLoadingGrid(false);
    }
  };

  const narrate = async () => {
    if (!scenarioId || !period) return;
    setLoadingNarrative(true);
    try {
      const res = await authFetch(`${API}/narrate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scenario_id: scenarioId, period }),
      });
      const d = await res.json();
      setNarrative(d.narrative?.text || '(no narrative)');
    } finally {
      setLoadingNarrative(false);
    }
  };

  return (
    <div>
      <div style={PANEL}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-hint)', marginBottom: 4 }}>Scenario</div>
            <select
              value={scenarioId || ''}
              onChange={e => setScenarioId(Number(e.target.value) || null)}
              style={{ padding: '6px 10px', fontSize: 13, borderRadius: 4, border: '1px solid var(--border-default)' }}
            >
              <option value="">Select…</option>
              {scenarios.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-hint)', marginBottom: 4 }}>Period (YYYY-MM)</div>
            <input
              value={period}
              onChange={e => setPeriod(e.target.value)}
              placeholder="2026-03"
              style={{ padding: '6px 10px', fontSize: 13, borderRadius: 4, border: '1px solid var(--border-default)' }}
            />
          </div>
          <SmallBtn primary onClick={load} disabled={!scenarioId || !period || loadingGrid}>
            {loadingGrid ? 'Loading…' : 'Load BvA'}
          </SmallBtn>
          <SmallBtn onClick={narrate} disabled={!scenarioId || !period || loadingNarrative}>
            {loadingNarrative ? 'Narrating…' : 'Composite narrative'}
          </SmallBtn>
        </div>
      </div>

      {grid.length > 0 && (
        <div style={PANEL}>
          <div style={SECTION_TITLE}>Variance grid — {period}</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--surface-muted, #f8fafc)' }}>
                <th style={thStyle}>Metric</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actuals</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Plan</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Variance</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Var %</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Forecast</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>FvB</th>
              </tr>
            </thead>
            <tbody>
              {grid.map(r => (
                <tr key={r.metric}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{r.metric}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{formatAmount(r.actuals)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{formatAmount(r.plan)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: r.variance < 0 ? '#b91c1c' : '#15803d' }}>
                    {formatAmount(r.variance)}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: (r.var_pct ?? 0) < 0 ? '#b91c1c' : '#15803d' }}>
                    {r.var_pct === null ? '—' : `${r.var_pct.toFixed(1)}%`}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{formatAmount(r.forecast)}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{formatAmount(r.fvb)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {narrative && (
        <div style={PANEL}>
          <div style={SECTION_TITLE}>Composite narrative</div>
          <div style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--text-primary, #1f2937)' }}>
            {narrative}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Ask tab ────────────────────────────────────────────────────────

function AskTab({ scenarios }: { scenarios: Scenario[] }) {
  const [prompt, setPrompt] = useState('');
  const [answer, setAnswer] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [tools, setTools] = useState<PlanTool[]>([]);
  const [historyQ, setHistoryQ] = useState('');
  const [historyAnswer, setHistoryAnswer] = useState<any>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [proposeFor, setProposeFor] = useState<number | null>(null);
  const [proposeResult, setProposeResult] = useState<any>(null);
  const [proposing, setProposing] = useState(false);

  useEffect(() => {
    (async () => {
      const res = await authFetch(`${API}/tools`);
      if (res.ok) {
        const d = await res.json();
        setTools(d.tools || []);
      }
    })();
  }, []);

  const ask = async () => {
    if (!prompt.trim()) return;
    setLoading(true); setAnswer(null);
    try {
      const res = await authFetch(`${API}/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt }),
      });
      const d = await res.json();
      setAnswer(d);
    } finally {
      setLoading(false);
    }
  };

  const askHistory = async () => {
    if (!historyQ.trim()) return;
    setLoadingHistory(true); setHistoryAnswer(null);
    try {
      const res = await authFetch(`${API}/history/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: historyQ }),
      });
      setHistoryAnswer(await res.json());
    } finally {
      setLoadingHistory(false);
    }
  };

  const propose = async () => {
    if (!proposeFor || !prompt.trim()) return;
    setProposing(true); setProposeResult(null);
    try {
      const res = await authFetch(`${API}/scenarios/${proposeFor}/propose-edits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt, actor: 'OPERATOR' }),
      });
      setProposeResult(await res.json());
    } finally {
      setProposing(false);
    }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16 }}>
      <div>
        <div style={PANEL}>
          <div style={SECTION_TITLE}>Ask the plan</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); } }}
              placeholder="What is the runway in December? / Compare BASE vs UPSIDE for revenue / What if SAR strengthens 5%?"
              style={{ flex: 1, padding: '8px 12px', fontSize: 13, borderRadius: 4, border: '1px solid var(--border-default)' }}
            />
            <SmallBtn primary onClick={ask} disabled={loading || !prompt.trim()}>
              {loading ? 'Asking…' : 'Ask'}
            </SmallBtn>
          </div>
          <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: 'var(--text-hint)' }}>Propose edits to:</span>
            <select
              value={proposeFor || ''}
              onChange={e => setProposeFor(Number(e.target.value) || null)}
              style={{ padding: '4px 8px', fontSize: 12, borderRadius: 4, border: '1px solid var(--border-default)' }}
            >
              <option value="">(read-only)</option>
              {scenarios.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            <SmallBtn onClick={propose} disabled={!proposeFor || !prompt.trim() || proposing}>
              {proposing ? 'Staging…' : 'Stage edits'}
            </SmallBtn>
          </div>
        </div>

        {answer && (
          <div style={PANEL}>
            <div style={SECTION_TITLE}>Answer</div>
            <div style={{ fontSize: 14, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
              {answer.answer || answer.fallback?.answer || '(no answer)'}
            </div>
            {answer.tool_trace && answer.tool_trace.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-hint)', marginBottom: 4 }}>
                  Tool calls ({answer.tool_trace.length})
                </div>
                {answer.tool_trace.map((t: any, i: number) => (
                  <div key={i} style={{ fontSize: 11, fontFamily: 'monospace', padding: '4px 0' }}>
                    → {t.tool}({Object.keys(t.args || {}).join(', ')})
                    {t.error && <span style={{ color: '#b91c1c' }}> · {t.error}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {proposeResult && (
          <div style={PANEL}>
            <div style={SECTION_TITLE}>
              Proposed edits ({proposeResult.staged_count || 0} staged)
              {proposeResult.batch_id && (
                <code style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-hint)' }}>
                  batch {proposeResult.batch_id.slice(0, 8)}
                </code>
              )}
            </div>
            {proposeResult.summary && (
              <div style={{ fontSize: 13, marginBottom: 12 }}>{proposeResult.summary}</div>
            )}
            {proposeResult.edits_preview && proposeResult.edits_preview.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--surface-muted, #f8fafc)' }}>
                    <th style={thStyle}>Action</th>
                    <th style={thStyle}>Field</th>
                    <th style={thStyle}>After</th>
                    <th style={thStyle}>Rationale</th>
                  </tr>
                </thead>
                <tbody>
                  {proposeResult.edits_preview.map((e: any, i: number) => (
                    <tr key={i}>
                      <td style={tdStyle}>{e.action}</td>
                      <td style={tdStyle}>{e.field_name}</td>
                      <td style={tdStyle}>{fmtVal(e.after_value)}</td>
                      <td style={{ ...tdStyle, fontSize: 12, color: 'var(--text-hint)' }}>{e.rationale}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ fontSize: 12, color: 'var(--text-hint)' }}>
                No edits returned. Reason: {proposeResult.reason || '—'}
              </div>
            )}
          </div>
        )}

        <div style={PANEL}>
          <div style={SECTION_TITLE}>Search plan history</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              value={historyQ}
              onChange={e => setHistoryQ(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); askHistory(); } }}
              placeholder="When did marketing % drop? / Show approved scenarios this month."
              style={{ flex: 1, padding: '8px 12px', fontSize: 13, borderRadius: 4, border: '1px solid var(--border-default)' }}
            />
            <SmallBtn onClick={askHistory} disabled={loadingHistory || !historyQ.trim()}>
              {loadingHistory ? 'Searching…' : 'Search'}
            </SmallBtn>
          </div>
          {historyAnswer && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 14, lineHeight: 1.55 }}>{historyAnswer.answer?.text}</div>
              {historyAnswer.answer?.citations && historyAnswer.answer.citations.length > 0 && (
                <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-hint)' }}>
                  Citations: {historyAnswer.answer.citations.join(', ')}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div style={PANEL}>
        <div style={SECTION_TITLE}>Available tools ({tools.length})</div>
        <div style={{ maxHeight: 600, overflow: 'auto' }}>
          {tools.map(t => (
            <div key={t.name} style={{ padding: '8px 0', borderBottom: '1px solid var(--border-subtle)' }}>
              <div style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600 }}>
                {t.name}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-hint)', marginTop: 2 }}>
                {t.description}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Drafts tab ─────────────────────────────────────────────────────

function DraftsTab({ toast }: { toast: (msg: string, ok?: boolean) => void }) {
  const [status, setStatus] = useState<'STAGED' | 'PROMOTED' | 'DISCARDED'>('STAGED');
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/drafts?status=${status}&limit=200`);
      if (res.ok) {
        const d = await res.json();
        setDrafts(d.drafts || []);
      }
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  // Group by batch
  const batches = useMemo(() => {
    const m = new Map<string, Draft[]>();
    drafts.forEach(d => {
      if (!m.has(d.batch_id)) m.set(d.batch_id, []);
      m.get(d.batch_id)!.push(d);
    });
    return Array.from(m.entries());
  }, [drafts]);

  const promote = async (batchId: string) => {
    setBusy(batchId);
    try {
      const res = await authFetch(`${API}/drafts/${batchId}/promote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ actor: 'OPERATOR' }),
      });
      const d = await res.json();
      if (d.ok) {
        toast(`Promoted ${d.applied} edits`);
      } else {
        toast(`Promote failed: ${d.reason || 'error'}`, false);
      }
      load();
    } finally {
      setBusy(null);
    }
  };

  const discard = async (batchId: string) => {
    setBusy(batchId);
    try {
      const res = await authFetch(`${API}/drafts/${batchId}`, { method: 'DELETE' });
      const d = await res.json();
      if (d.ok) {
        toast('Discarded');
      } else {
        toast(`Discard failed: ${d.reason}`, false);
      }
      load();
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <div style={PANEL}>
        <div style={{ display: 'flex', gap: 8 }}>
          {(['STAGED', 'PROMOTED', 'DISCARDED'] as const).map(s => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              style={{
                padding: '6px 12px', fontSize: 12, fontWeight: 600,
                borderRadius: 4, cursor: 'pointer',
                border: '1px solid var(--border-default)',
                background: status === s ? 'var(--navy-900)' : '#fff',
                color: status === s ? '#fff' : 'var(--navy-900)',
              }}
            >{s}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 24 }}>Loading drafts…</div>
      ) : batches.length === 0 ? (
        <div style={PANEL}>No {status.toLowerCase()} drafts.</div>
      ) : batches.map(([batchId, rows]) => (
        <div key={batchId} style={PANEL}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <div>
              <code style={{ fontSize: 12, color: 'var(--text-hint)' }}>batch {batchId.slice(0, 12)}</code>
              <span style={{ marginLeft: 12, fontSize: 12 }}>
                {rows.length} edit{rows.length === 1 ? '' : 's'} ·
                scenario {rows[0]?.scenario_id} ·
                {shortDate(rows[0]?.created_at)}
              </span>
            </div>
            {status === 'STAGED' && (
              <div style={{ display: 'flex', gap: 6 }}>
                <SmallBtn primary onClick={() => promote(batchId)} disabled={busy === batchId}>
                  {busy === batchId ? 'Working…' : 'Promote'}
                </SmallBtn>
                <SmallBtn onClick={() => discard(batchId)} disabled={busy === batchId}>
                  Discard
                </SmallBtn>
              </div>
            )}
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--surface-muted, #f8fafc)' }}>
                <th style={thStyle}>Action</th>
                <th style={thStyle}>Field</th>
                <th style={thStyle}>Before → After</th>
                <th style={thStyle}>Rationale</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id}>
                  <td style={tdStyle}>{r.action}</td>
                  <td style={tdStyle}>{r.field}</td>
                  <td style={tdStyle}>
                    <span style={{ color: 'var(--text-hint)' }}>{fmtVal(r.before)}</span>
                    {' → '}
                    <span style={{ fontWeight: 600 }}>{fmtVal(r.after)}</span>
                  </td>
                  <td style={{ ...tdStyle, fontSize: 12, color: 'var(--text-hint)' }}>{r.rationale}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

// ── Dimensional tab ──────────────────────────────────────────────

function DimensionalTab({ scenarios }: { scenarios: Scenario[] }) {
  const [scenarioId, setScenarioId] = useState<number | null>(null);
  const [sub, setSub] = useState<'workforce' | 'capex' | 'projects' | 'sales' | 'tax' | 'lrp'>('workforce');
  const [rows, setRows] = useState<any[]>([]);
  const [agg, setAgg] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addToast, setAddToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const showAddToast = useCallback((msg: string, ok = true) => {
    setAddToast({ msg, ok });
    setTimeout(() => setAddToast(null), 3500);
  }, []);

  useEffect(() => {
    if (scenarios.length && !scenarioId) setScenarioId(scenarios[0].id);
  }, [scenarios, scenarioId]);

  const load = useCallback(async () => {
    if (!scenarioId) return;
    setLoading(true);
    try {
      const listRes = await authFetch(`${API}/${sub}?scenario_id=${scenarioId}`);
      const listJson = await listRes.json();
      let listRows: any[] = [];
      if (sub === 'workforce') listRows = listJson.positions || [];
      else if (sub === 'capex') listRows = listJson.assets || [];
      else if (sub === 'projects') listRows = listJson.projects || [];
      else if (sub === 'sales') listRows = listJson.opportunities || [];
      else if (sub === 'tax') listRows = listJson.jurisdictions || [];
      else if (sub === 'lrp') listRows = listJson.horizons || [];
      setRows(listRows);

      if (sub !== 'tax' && sub !== 'lrp') {
        const aRes = await authFetch(`${API}/${sub}/aggregate?scenario_id=${scenarioId}`);
        setAgg(await aRes.json());
      } else if (sub === 'lrp') {
        const pRes = await authFetch(`${API}/lrp/projection?scenario_id=${scenarioId}`);
        setAgg(await pRes.json());
      } else {
        setAgg(null);
      }
    } finally {
      setLoading(false);
    }
  }, [scenarioId, sub]);

  useEffect(() => { load(); }, [load]);

  const subs: Array<typeof sub> = ['workforce', 'capex', 'projects', 'sales', 'tax', 'lrp'];

  return (
    <div>
      <div style={PANEL}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-hint)', marginBottom: 4 }}>Scenario</div>
            <select
              value={scenarioId || ''}
              onChange={e => setScenarioId(Number(e.target.value) || null)}
              style={{ padding: '6px 10px', fontSize: 13, borderRadius: 4, border: '1px solid var(--border-default)' }}
            >
              {scenarios.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 4, marginLeft: 12 }}>
            {subs.map(s => (
              <button
                key={s}
                onClick={() => setSub(s)}
                style={{
                  padding: '6px 12px', fontSize: 12, fontWeight: 600,
                  borderRadius: 4, cursor: 'pointer',
                  border: '1px solid var(--border-default)',
                  background: sub === s ? 'var(--navy-900)' : '#fff',
                  color: sub === s ? '#fff' : 'var(--navy-900)',
                }}
              >{s}</button>
            ))}
          </div>
        </div>
      </div>

      {loading && <div style={{ padding: 24 }}>Loading…</div>}

      {!loading && (
        <>
          <div style={PANEL}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={SECTION_TITLE}>{sub} ({rows.length})</div>
              <SmallBtn primary onClick={() => setShowAdd(s => !s)} disabled={!scenarioId}>
                {showAdd ? 'Cancel' : '+ Add'}
              </SmallBtn>
            </div>
            {showAdd && scenarioId && (
              <AddRowForm
                sub={sub}
                scenarioId={scenarioId}
                onClose={() => setShowAdd(false)}
                onCreated={() => { setShowAdd(false); load(); }}
                toast={showAddToast}
              />
            )}
            {rows.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text-hint)' }}>
                No {sub} rows yet. Click + Add above to create the first one.
              </div>
            ) : (
              <div style={{ maxHeight: 360, overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--surface-muted, #f8fafc)' }}>
                      {Object.keys(rows[0]).slice(0, 8).map(k => (
                        <th key={k} style={thStyle}>{k}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={r.id ?? i}>
                        {Object.keys(rows[0]).slice(0, 8).map(k => (
                          <td key={k} style={tdStyle}>{fmtVal(r[k])}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {agg && (sub !== 'tax') && (
            <div style={PANEL}>
              <div style={SECTION_TITLE}>
                {sub === 'lrp' ? 'Projection by horizon' : 'Aggregate by period'}
              </div>
              {sub === 'lrp' ? (
                (agg.horizons || []).map((h: any) => (
                  <div key={h.id} style={{ marginBottom: 12 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>
                      {h.label} · {h.scenario_type} · {h.horizon_years}y
                    </div>
                    <table style={{ width: '100%', marginTop: 6, borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: 'var(--surface-muted, #f8fafc)' }}>
                          <th style={thStyle}>Year</th>
                          <th style={thStyle}>Revenue</th>
                          <th style={thStyle}>Growth</th>
                          <th style={thStyle}>EBITDA %</th>
                          <th style={thStyle}>EBITDA</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(h.years || []).map((y: any) => (
                          <tr key={y.year_offset}>
                            <td style={tdStyle}>{y.fiscal_year || `Y${y.year_offset}`}</td>
                            <td style={tdStyle}>{formatAmount(y.revenue)}</td>
                            <td style={tdStyle}>{(y.growth_rate * 100).toFixed(1)}%</td>
                            <td style={tdStyle}>{(y.ebitda_pct * 100).toFixed(1)}%</td>
                            <td style={tdStyle}>{formatAmount(y.ebitda)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))
              ) : (
                <div style={{ maxHeight: 240, overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'var(--surface-muted, #f8fafc)' }}>
                        {(agg.periods?.[0] ? Object.keys(agg.periods[0]) : []).map((k: string) => (
                          <th key={k} style={thStyle}>{k}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(agg.periods || []).map((p: any, i: number) => (
                        <tr key={i}>
                          {Object.keys(p).map(k => (
                            <td key={k} style={tdStyle}>{typeof p[k] === 'number' ? formatAmount(p[k]) : p[k]}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {addToast && (
        <div style={{
          position: 'fixed', bottom: 70, right: 20, padding: '10px 16px',
          borderRadius: 6, fontSize: 13, fontWeight: 600,
          background: addToast.ok ? '#d1fae5' : '#fee2e2',
          color: addToast.ok ? '#047857' : '#991b1b',
          border: `1px solid ${addToast.ok ? '#34d399' : '#fca5a5'}`,
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          zIndex: 50,
        }}>
          {addToast.msg}
        </div>
      )}
    </div>
  );
}

// ── AddRowForm (generic Dimensional sub-tab row creator) ────────────

interface FormFieldConfig {
  key: string;
  label: string;
  type: 'text' | 'number' | 'select';
  required?: boolean;
  placeholder?: string;
  options?: string[];
}

const SUB_FORM_CONFIG: Record<string, FormFieldConfig[]> = {
  workforce: [
    { key: 'role', label: 'Role', type: 'text', required: true, placeholder: 'Senior Engineer' },
    { key: 'department', label: 'Department', type: 'text', placeholder: 'Engineering' },
    { key: 'level', label: 'Level', type: 'text', placeholder: 'L3 / Manager' },
    { key: 'start_period', label: 'Start period', type: 'text', placeholder: '2026-01', required: true },
    { key: 'end_period', label: 'End period', type: 'text', placeholder: '2027-12 (or blank)' },
    { key: 'base_salary_annual', label: 'Base salary (annual)', type: 'number', placeholder: '150000', required: true },
    { key: 'bonus_pct', label: 'Bonus %', type: 'number', placeholder: '0.10' },
    { key: 'benefits_pct', label: 'Benefits %', type: 'number', placeholder: '0.20' },
    { key: 'employer_tax_pct', label: 'Employer tax %', type: 'number', placeholder: '0.08' },
    { key: 'equity_pct', label: 'Equity %', type: 'number', placeholder: '0.05' },
    { key: 'status', label: 'Status', type: 'select', options: ['PLANNED', 'OPEN', 'FILLED', 'CLOSED'] },
  ],
  capex: [
    { key: 'name', label: 'Asset name', type: 'text', required: true, placeholder: 'Production line #3' },
    { key: 'category', label: 'Category', type: 'text', placeholder: 'Equipment / IT / Real Estate' },
    { key: 'purchase_period', label: 'Purchase period', type: 'text', placeholder: '2026-01', required: true },
    { key: 'in_service_period', label: 'In-service period', type: 'text', placeholder: '2026-02 (defaults to purchase)' },
    { key: 'amount', label: 'Amount', type: 'number', placeholder: '500000', required: true },
    { key: 'useful_life_years', label: 'Useful life (years)', type: 'number', placeholder: '5' },
    { key: 'salvage_value', label: 'Salvage value', type: 'number', placeholder: '0' },
  ],
  projects: [
    { key: 'name', label: 'Project name', type: 'text', required: true, placeholder: 'Platform refactor Q2' },
    { key: 'project_code', label: 'Project code', type: 'text', placeholder: 'PRJ-001' },
    { key: 'category', label: 'Category', type: 'text', placeholder: 'Engineering / Infra' },
    { key: 'department', label: 'Department', type: 'text' },
    { key: 'start_period', label: 'Start period', type: 'text', placeholder: '2026-01', required: true },
    { key: 'end_period', label: 'End period', type: 'text', placeholder: '2026-12' },
    { key: 'total_budget', label: 'Total budget', type: 'number', placeholder: '250000', required: true },
    { key: 'capex_portion', label: 'CapEx portion', type: 'number', placeholder: '0' },
    { key: 'status', label: 'Status', type: 'select', options: ['PLANNED', 'ACTIVE', 'COMPLETED', 'CANCELLED'] },
  ],
  sales: [
    { key: 'customer_name', label: 'Customer', type: 'text', required: true, placeholder: 'Acme Corp' },
    { key: 'opportunity_code', label: 'Opportunity code', type: 'text', placeholder: 'OPP-2026-001' },
    { key: 'product', label: 'Product', type: 'text', placeholder: 'Enterprise plan' },
    { key: 'stage', label: 'Stage', type: 'text', placeholder: 'PROPOSAL / NEGOTIATION' },
    { key: 'close_period', label: 'Close period', type: 'text', placeholder: '2026-03', required: true },
    { key: 'amount', label: 'Amount', type: 'number', placeholder: '100000', required: true },
    { key: 'win_probability', label: 'Win prob (0-1)', type: 'number', placeholder: '0.5' },
    { key: 'status', label: 'Status', type: 'select', options: ['OPEN', 'CLOSED_WON', 'CLOSED_LOST'] },
    { key: 'owner', label: 'Owner', type: 'text', placeholder: 'Sales rep name' },
  ],
  tax: [
    { key: 'entity_code', label: 'Entity code', type: 'text', placeholder: '1000' },
    { key: 'jurisdiction_name', label: 'Jurisdiction', type: 'text', required: true, placeholder: 'US Federal / California / Saudi Arabia' },
    { key: 'jurisdiction_type', label: 'Type', type: 'select', options: ['FEDERAL', 'STATE', 'FOREIGN'] },
    { key: 'tax_rate', label: 'Tax rate (0-1)', type: 'number', required: true, placeholder: '0.21' },
    { key: 'nol_carryforward', label: 'NOL carryforward', type: 'number', placeholder: '0' },
    { key: 'deferred_tax_balance', label: 'Deferred tax balance', type: 'number', placeholder: '0' },
  ],
  lrp: [
    { key: 'label', label: 'Label', type: 'text', required: true, placeholder: '5-year target' },
    { key: 'scenario_type', label: 'Type', type: 'select', options: ['CONSERVATIVE', 'TARGET', 'AGGRESSIVE'] },
    { key: 'horizon_years', label: 'Horizon years', type: 'number', placeholder: '5' },
    { key: 'start_fiscal_year', label: 'Start FY', type: 'number', placeholder: '2026' },
    { key: 'baseline_revenue', label: 'Baseline revenue', type: 'number', placeholder: '5000000' },
    { key: 'target_revenue_y1', label: 'Y1 revenue', type: 'number' },
    { key: 'target_revenue_y3', label: 'Y3 revenue', type: 'number' },
    { key: 'target_revenue_y5', label: 'Y5 revenue', type: 'number' },
    { key: 'target_ebitda_pct_y1', label: 'Y1 EBITDA %', type: 'number', placeholder: '0.10' },
    { key: 'target_ebitda_pct_y3', label: 'Y3 EBITDA %', type: 'number', placeholder: '0.15' },
    { key: 'target_ebitda_pct_y5', label: 'Y5 EBITDA %', type: 'number', placeholder: '0.20' },
  ],
};

function AddRowForm({
  sub, scenarioId, onClose, onCreated, toast,
}: {
  sub: string;
  scenarioId: number;
  onClose: () => void;
  onCreated: () => void;
  toast: (msg: string, ok?: boolean) => void;
}) {
  const config = SUB_FORM_CONFIG[sub] || [];
  const [values, setValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const handleChange = (key: string, raw: string) => {
    setValues(prev => ({ ...prev, [key]: raw }));
  };

  const submit = async () => {
    // Validate required
    const missing = config.filter(f => f.required && !values[f.key]?.toString().trim());
    if (missing.length) {
      toast(`Missing: ${missing.map(f => f.label).join(', ')}`, false);
      return;
    }
    setSaving(true);
    try {
      const body: Record<string, any> = { scenario_id: scenarioId, actor: 'OPERATOR' };
      for (const [k, raw] of Object.entries(values)) {
        if (raw === '' || raw == null) continue;
        const cfg = config.find(c => c.key === k);
        if (cfg?.type === 'number') {
          const num = Number(raw);
          body[k] = Number.isFinite(num) ? num : raw;
        } else {
          body[k] = raw;
        }
      }
      const res = await authFetch(`${API}/${sub}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        toast(`Created ${sub} row #${data.id}`);
        onCreated();
      } else {
        toast(`Create failed: ${data.detail || data.error || 'error'}`, false);
      }
    } catch (e: any) {
      toast(`Create error: ${e.message}`, false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      background: 'var(--surface-muted, #f8fafc)',
      border: '1px solid var(--border-subtle)',
      borderRadius: 6,
      padding: 12,
      marginBottom: 12,
    }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
        gap: 10,
      }}>
        {config.map(f => (
          <div key={f.key}>
            <div style={{
              fontSize: 10, fontWeight: 600, color: 'var(--text-hint)',
              textTransform: 'uppercase', letterSpacing: 0.04, marginBottom: 3,
            }}>
              {f.label}{f.required && <span style={{ color: '#b91c1c' }}>*</span>}
            </div>
            {f.type === 'select' ? (
              <select
                title={f.label}
                value={values[f.key] || ''}
                onChange={e => handleChange(f.key, e.target.value)}
                style={{ ...inputStyle, fontSize: 12, padding: '4px 8px' }}
              >
                <option value="">—</option>
                {(f.options || []).map(o => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            ) : (
              <input
                type={f.type === 'number' ? 'number' : 'text'}
                title={f.label}
                placeholder={f.placeholder || ''}
                value={values[f.key] || ''}
                onChange={e => handleChange(f.key, e.target.value)}
                style={{ ...inputStyle, fontSize: 12, padding: '4px 8px' }}
              />
            )}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
        <SmallBtn onClick={onClose}>Cancel</SmallBtn>
        <SmallBtn primary onClick={submit} disabled={saving}>
          {saving ? 'Creating…' : `Create ${sub}`}
        </SmallBtn>
      </div>
    </div>
  );
}

// ── small helpers ──────────────────────────────────────────────────

function StateBadge({ status }: { status: string }) {
  const c = STATE_COLORS[status] || STATE_COLORS.DRAFT;
  return (
    <span style={{
      padding: '2px 8px', fontSize: 11, fontWeight: 600, borderRadius: 10,
      background: c.bg, color: c.fg, border: `1px solid ${c.border}`,
    }}>{status}</span>
  );
}

function SourceBadge({ source }: { source: string }) {
  return (
    <span style={{
      padding: '2px 6px', fontSize: 10, fontWeight: 600, borderRadius: 4,
      background: 'transparent', color: SOURCE_COLORS[source] || '#475569',
      border: `1px solid ${SOURCE_COLORS[source] || '#cbd5e1'}`,
    }}>{source}</span>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div style={PANEL}>
      <div style={{ fontSize: 11, color: 'var(--text-hint)', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--navy-900)' }}>{value}</div>
    </div>
  );
}

function SmallBtn({
  children, onClick, primary, disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  primary?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '6px 12px', fontSize: 12, fontWeight: 600,
        borderRadius: 4, cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        background: primary ? 'var(--navy-900, #1e3a5f)' : '#fff',
        color: primary ? '#fff' : 'var(--navy-900, #1e3a5f)',
        border: primary ? 'none' : '1px solid var(--border-default, #cbd5e1)',
        transition: 'all 0.15s',
      }}
    >{children}</button>
  );
}

function fmtVal(v: any): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'number') return Number.isInteger(v) ? String(v) : v.toFixed(4);
  if (typeof v === 'object') return JSON.stringify(v).slice(0, 60);
  const s = String(v);
  return s.length > 60 ? s.slice(0, 57) + '…' : s;
}

function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return iso.slice(0, 16).replace('T', ' ');
}

// ── NowTab (default landing dashboard) ─────────────────────────────

function NowTab({
  scenarios, loading, reload, toast, goToTab,
}: {
  scenarios: Scenario[];
  loading: boolean;
  reload: () => void;
  toast: (msg: string, ok?: boolean) => void;
  goToTab: (key: string) => void;
}) {
  // Pick the most-recently-computed ACTIVE scenario for the summary tiles
  const primaryScenario = useMemo(() => {
    const active = scenarios.filter(s => s.is_active && s.has_computed_outputs);
    if (active.length) return active[0]; // /scenarios is already id-desc
    return scenarios.find(s => s.has_computed_outputs) || scenarios[0] || null;
  }, [scenarios]);

  const [computedSummary, setComputedSummary] = useState<any>(null);
  const [runwayTraj, setRunwayTraj] = useState<any>(null);
  const [narrative, setNarrative] = useState<string | null>(null);
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [recentBva, setRecentBva] = useState<any>(null);

  useEffect(() => {
    if (!primaryScenario?.id || !primaryScenario.has_computed_outputs) {
      setComputedSummary(null);
      setRunwayTraj(null);
      setRecentBva(null);
      return;
    }
    (async () => {
      try {
        const [coRes, rwRes] = await Promise.all([
          authFetch(`${API}/scenarios/${primaryScenario.id}/computed`),
          authFetch(`${API}/scenarios/${primaryScenario.id}/computed`),
        ]);
        if (coRes.ok) {
          const d = await coRes.json();
          setComputedSummary(d);
        }
        if (rwRes.ok) {
          const d = await rwRes.json();
          // pull runway trajectory from periods
          const traj = (d.periods || []).map((p: any) => ({
            period: p.period,
            ending_cash: p.metrics?.ending_cash || 0,
            runway_months: p.metrics?.runway_months || null,
          }));
          setRunwayTraj({ last: traj[traj.length - 1] || null, traj });
        }
      } catch (e) {
        console.error('NowTab computed load failed', e);
      }
    })();
  }, [primaryScenario]);

  // Find the most recent month with both actuals and a planned value (best
  // candidate for BvA snapshot card). Default: prior calendar month.
  const recentPeriod = useMemo(() => {
    const today = new Date();
    let y = today.getFullYear();
    let m = today.getMonth(); // 0-indexed; -1 month = prior calendar
    if (m === 0) { y -= 1; m = 12; } // wrap
    return `${y}-${String(m).padStart(2, '0')}`;
  }, []);

  useEffect(() => {
    if (!primaryScenario?.id) return;
    (async () => {
      try {
        const r = await authFetch(
          `${API}/bva?period=${recentPeriod}&scenario_id=${primaryScenario.id}`,
        );
        if (r.ok) setRecentBva(await r.json());
      } catch (e) { console.error(e); }
    })();
  }, [primaryScenario, recentPeriod]);

  const runNarrate = async () => {
    if (!primaryScenario?.id) return;
    setNarrativeLoading(true);
    try {
      const r = await authFetch(`${API}/narrate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scenario_id: primaryScenario.id, period: recentPeriod }),
      });
      if (r.ok) {
        const d = await r.json();
        setNarrative(d.narrative?.text || '(no narrative)');
      } else {
        toast('Narrative failed', false);
      }
    } finally {
      setNarrativeLoading(false);
    }
  };

  const totals = computedSummary?.totals || {};

  return (
    <div>
      <OverviewBar scenarios={scenarios} loading={loading} />
      <WhatNowPanel
        scenarios={scenarios}
        reload={reload}
        toast={toast}
        goToTab={goToTab}
      />

      {primaryScenario && (
        <div style={PANEL}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
            <div>
              <div style={SECTION_TITLE}>Primary scenario · {primaryScenario.name}</div>
              <div style={{ fontSize: 11, color: 'var(--text-hint)' }}>
                {primaryScenario.scenario_type} · FY{primaryScenario.fiscal_year} ·
                ID {primaryScenario.id} ·
                {primaryScenario.has_computed_outputs ? ' has computed_outputs' : ' not computed yet'}
              </div>
            </div>
            <SmallBtn onClick={() => goToTab('scenarios')}>Open detail</SmallBtn>
          </div>

          {!primaryScenario.has_computed_outputs ? (
            <div style={{ fontSize: 12, color: 'var(--text-hint)' }}>
              No computed_outputs yet. Open the Scenarios tab, set assumptions
              or seed from actuals, then Compute scenario.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 12 }}>
              <KpiTile
                label="Revenue (FY total)"
                value={formatAmount(totals.revenue || 0)}
                sub={`gross profit ${formatAmount(totals.gross_profit || 0)}`}
              />
              <KpiTile
                label="EBITDA (FY total)"
                value={formatAmount(totals.ebitda || 0)}
                sub={
                  totals.revenue
                    ? `${((totals.ebitda || 0) / totals.revenue * 100).toFixed(1)}% margin`
                    : ''
                }
              />
              <KpiTile
                label="Net income (FY total)"
                value={formatAmount(totals.net_income || 0)}
                sub={`tax ${formatAmount(totals.tax_provision || 0)}`}
              />
              <KpiTile
                label="Ending cash (last period)"
                value={formatAmount(runwayTraj?.last?.ending_cash || 0)}
                sub={
                  runwayTraj?.last?.runway_months !== null && runwayTraj?.last?.runway_months !== undefined
                    ? `runway ${runwayTraj.last.runway_months >= 999 ? 'inf' : runwayTraj.last.runway_months.toFixed(1) + 'mo'}`
                    : 'runway —'
                }
              />
            </div>
          )}

          {recentBva?.ok && recentBva.rows?.length > 0 && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed var(--border-subtle)' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: 0.04, marginBottom: 8 }}>
                BvA — {recentPeriod}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--surface-muted, #f8fafc)' }}>
                    <th style={thStyle}>Metric</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Actuals</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Plan</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Variance</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Var %</th>
                  </tr>
                </thead>
                <tbody>
                  {recentBva.rows.slice(0, 4).map((r: any) => (
                    <tr key={r.metric}>
                      <td style={{ ...tdStyle, fontWeight: 600 }}>{r.metric}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{formatAmount(r.actuals)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{formatAmount(r.plan)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: r.variance < 0 ? '#b91c1c' : '#15803d' }}>
                        {formatAmount(r.variance)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: (r.var_pct ?? 0) < 0 ? '#b91c1c' : '#15803d' }}>
                        {r.var_pct === null ? '—' : `${r.var_pct.toFixed(1)}%`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                <SmallBtn onClick={() => goToTab('bva')}>Open BvA tab</SmallBtn>
                <SmallBtn onClick={runNarrate} disabled={narrativeLoading}>
                  {narrativeLoading ? 'Narrating…' : 'Composite narrative'}
                </SmallBtn>
              </div>
              {narrative && (
                <div style={{ marginTop: 10, padding: 10, background: 'var(--surface-muted, #f8fafc)', borderRadius: 4, fontSize: 13, lineHeight: 1.5 }}>
                  {narrative}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── LinesTab (the budget grid -- rows by accounts, cols by periods) ─

interface LineCell {
  amount: number;
  source: string;
  line_id: number | null;
  cost_center?: string | null;
  department?: string | null;
  snapshot_amount?: number;
  delta?: number;
  delta_pct?: number | null;
}

interface LineRow {
  gl_account: string;
  account_name: string | null;
  account_type: string | null;
  statement: string | null;
  display_order: number | null;
  cost_center: string | null;
  department: string | null;
  row_total: number;
  cells: Record<string, LineCell>;
}

interface LinesGrid {
  ok: boolean;
  scenario_id: number;
  scenario_name: string;
  scenario_status: string;
  fiscal_year: number | null;
  periods: string[];
  rows: LineRow[];
  period_totals: Record<string, number>;
  band_totals: Record<string, Record<string, number>>;
  compare: any;
  source_counts: Record<string, number>;
  error?: string;
  warning?: string;
}

function LinesTab({
  scenarios, toast,
}: {
  scenarios: Scenario[];
  toast: (msg: string, ok?: boolean) => void;
}) {
  const [scenarioId, setScenarioId] = useState<number | null>(null);
  const [grid, setGrid] = useState<LinesGrid | null>(null);
  const [loading, setLoading] = useState(false);
  const [snapshots, setSnapshots] = useState<any[]>([]);
  const [compareSnapshotId, setCompareSnapshotId] = useState<number | null>(null);
  const [editingCell, setEditingCell] = useState<string | null>(null); // "lineId:period"
  const [draftValue, setDraftValue] = useState('');
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false); // double-save guard (Enter + Blur race)

  useEffect(() => {
    if (scenarios.length && !scenarioId) {
      setScenarioId(scenarios[0].id);
    }
  }, [scenarios, scenarioId]);

  const loadSnapshots = useCallback(async (sid: number) => {
    try {
      const r = await authFetch(`${API}/scenarios/${sid}/snapshots?limit=30`);
      if (r.ok) {
        const d = await r.json();
        setSnapshots(d.snapshots || []);
      }
    } catch (e) { console.error(e); }
  }, []);

  const load = useCallback(async () => {
    if (!scenarioId) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (compareSnapshotId) qs.set('compare_snapshot_id', String(compareSnapshotId));
      const url = `${API}/scenarios/${scenarioId}/lines${qs.toString() ? '?' + qs : ''}`;
      const r = await authFetch(url);
      if (r.ok) {
        const d = await r.json();
        setGrid(d);
      } else {
        const d = await r.json().catch(() => ({}));
        toast(`Load failed: ${d.detail || r.status}`, false);
      }
    } finally {
      setLoading(false);
    }
  }, [scenarioId, compareSnapshotId, toast]);

  useEffect(() => {
    if (scenarioId) {
      load();
      loadSnapshots(scenarioId);
    }
  }, [scenarioId, compareSnapshotId, load, loadSnapshots]);

  const onCellClick = (lineId: number | null, period: string, cell: LineCell) => {
    if (!lineId) return;
    setEditingCell(`${lineId}:${period}`);
    setDraftValue(String(cell.amount ?? ''));
  };

  const saveCell = async (lineId: number) => {
    // Ref-guard against the Enter+Blur double-save race
    if (savingRef.current) return;
    // Empty-string footgun -- cancel rather than silently writing 0
    if (draftValue.trim() === '') {
      cancelCell();
      return;
    }
    const num = Number(draftValue);
    if (!Number.isFinite(num)) {
      toast('Invalid number', false);
      return;
    }
    savingRef.current = true;
    setSaving(true);
    try {
      const r = await authFetch(`${API}/budget-lines/${lineId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ budget_amount: num, actor: 'OPERATOR' }),
      });
      const d = await r.json();
      if (r.ok && d.ok) {
        toast(`Saved ${num}`);
        setEditingCell(null);
        setDraftValue('');
        load();
      } else {
        toast(`Save failed: ${d.error || d.detail || r.status}`, false);
      }
    } finally {
      setSaving(false);
      savingRef.current = false;
    }
  };

  const cancelCell = () => {
    setEditingCell(null);
    setDraftValue('');
  };

  // Group rows by band
  const grouped = useMemo(() => {
    if (!grid || !grid.rows) return [] as Array<{ band: string; rows: LineRow[] }>;
    const out = new Map<string, LineRow[]>();
    grid.rows.forEach(r => {
      const order = r.display_order ?? 999;
      let band = 'OTHER';
      if (order >= 300 && order < 400) band = 'REVENUE';
      else if (order >= 400 && order < 500) band = 'COGS';
      else if (order >= 500 && order < 600) band = 'OPEX';
      else if (order >= 600 && order < 700) band = 'OTHER_PL';
      else if ((r.account_type || '').toUpperCase() === 'REVENUE') band = 'REVENUE';
      else if ((r.account_type || '').toUpperCase() === 'EXPENSE') band = 'OPEX';
      if (!out.has(band)) out.set(band, []);
      out.get(band)!.push(r);
    });
    const order = ['REVENUE', 'COGS', 'OPEX', 'OTHER_PL', 'OTHER'];
    return order
      .filter(b => out.has(b))
      .map(band => ({ band, rows: out.get(band)! }));
  }, [grid]);

  if (!scenarioId) {
    return <div style={{ padding: 24, color: 'var(--text-hint)' }}>No scenarios — create one first.</div>;
  }

  const periods = grid?.periods || [];

  return (
    <div>
      <div style={PANEL}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-hint)', marginBottom: 4 }}>Scenario</div>
            <select
              title="Scenario"
              value={scenarioId || ''}
              onChange={e => setScenarioId(Number(e.target.value) || null)}
              style={{ padding: '6px 10px', fontSize: 13, borderRadius: 4, border: '1px solid var(--border-default)' }}
            >
              {scenarios.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-hint)', marginBottom: 4 }}>Compare to snapshot</div>
            <select
              title="Snapshot for diff overlay"
              value={compareSnapshotId || ''}
              onChange={e => setCompareSnapshotId(Number(e.target.value) || null)}
              style={{ padding: '6px 10px', fontSize: 13, borderRadius: 4, border: '1px solid var(--border-default)' }}
            >
              <option value="">— none —</option>
              {snapshots.map(s => (
                <option key={s.id} value={s.id}>
                  #{s.id} {s.kind} {s.created_at ? `· ${s.created_at.slice(0, 10)}` : ''}
                </option>
              ))}
            </select>
          </div>
          <SmallBtn onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Reload'}</SmallBtn>
        </div>

        {grid?.source_counts && Object.keys(grid.source_counts).length > 0 && (
          <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 11 }}>
            <span style={{ color: 'var(--text-hint)', fontWeight: 600 }}>Cell sources:</span>
            {Object.entries(grid.source_counts).map(([src, count]) => (
              <span key={src} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{
                  display: 'inline-block', width: 8, height: 8, borderRadius: 2,
                  background: SOURCE_BAND_COLORS[src] || '#94a3b8',
                }} />
                {src}: {count}
              </span>
            ))}
          </div>
        )}
      </div>

      {grid?.compare?.note && (
        <div style={{
          marginBottom: 8, padding: 10, fontSize: 11,
          background: '#fef3c7', color: '#92400e', borderRadius: 4, border: '1px solid #fbbf24',
        }}>
          <strong>Snapshot comparison note:</strong> {grid.compare.note}
        </div>
      )}

      {grid?.warning && (
        <div style={{
          marginBottom: 8, padding: 10, fontSize: 12,
          background: '#fee2e2', color: '#991b1b', borderRadius: 4, border: '1px solid #fca5a5',
        }}>
          {grid.warning}
        </div>
      )}

      {loading && <div style={{ padding: 24 }}>Loading lines…</div>}

      {!loading && grid && grid.rows.length === 0 && (
        <div style={PANEL}>
          <div style={{ fontSize: 13, color: 'var(--text-hint)', lineHeight: 1.5 }}>
            No budget lines for this scenario yet. Use <strong>Seed from actuals</strong>,
            <strong> Upload document</strong>, or stage edits via <strong>Ask</strong> from
            the Scenarios tab. Then Promote the staged batch on the Drafts tab.
          </div>
        </div>
      )}

      {!loading && grid && grid.rows.length > 0 && (
        <div style={{ ...PANEL, padding: 0, overflow: 'hidden' }}>
          <div style={{ maxHeight: 600, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead style={{ position: 'sticky', top: 0, background: 'var(--surface-muted, #f8fafc)', zIndex: 1 }}>
                <tr>
                  <th style={{ ...thStyle, position: 'sticky', left: 0, background: 'var(--surface-muted, #f8fafc)', minWidth: 220, zIndex: 2 }}>
                    Account
                  </th>
                  {periods.map(p => (
                    <th key={p} style={{ ...thStyle, textAlign: 'right', minWidth: 100 }}>{p}</th>
                  ))}
                  <th style={{ ...thStyle, textAlign: 'right', background: 'var(--surface-muted, #f8fafc)' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {grouped.map(({ band, rows }) => (
                  <React.Fragment key={band}>
                    <tr style={{ background: 'var(--accent-subtle, #f1f5f9)' }}>
                      <td colSpan={periods.length + 2} style={{
                        padding: '6px 14px', fontSize: 11, fontWeight: 700,
                        color: 'var(--navy-900)', textTransform: 'uppercase', letterSpacing: 0.04,
                      }}>
                        {band}
                      </td>
                    </tr>
                    {rows.map((r, i) => (
                      <tr key={`${band}-${r.gl_account}-${r.cost_center || ''}-${r.department || ''}-${i}`}>
                        <td style={{
                          ...tdStyle, position: 'sticky', left: 0, background: '#fff',
                          fontSize: 12, fontWeight: 500,
                        }}>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ color: 'var(--navy-900)' }}>{r.gl_account}</span>
                            <span style={{ fontSize: 11, color: 'var(--text-hint)' }}>
                              {r.account_name || '(unnamed)'}
                              {r.cost_center ? ` · CC ${r.cost_center}` : ''}
                              {r.department ? ` · ${r.department}` : ''}
                            </span>
                          </div>
                        </td>
                        {periods.map(p => {
                          const cell = r.cells[p];
                          const cellKey = cell?.line_id ? `${cell.line_id}:${p}` : '';
                          const isEditing = editingCell === cellKey;
                          if (!cell) {
                            return (
                              <td key={p} style={{ ...tdStyle, textAlign: 'right', color: 'var(--text-hint)' }}>—</td>
                            );
                          }
                          const color = SOURCE_BAND_COLORS[cell.source] || '#94a3b8';
                          const delta = cell.delta;
                          return (
                            <td
                              key={p}
                              onClick={() => onCellClick(cell.line_id, p, cell)}
                              style={{
                                ...tdStyle, textAlign: 'right',
                                cursor: cell.line_id ? 'pointer' : 'default',
                                position: 'relative',
                                borderLeft: `3px solid ${color}`,
                              }}
                              title={`source: ${cell.source}${cell.line_id ? ` · line_id ${cell.line_id}` : ''}${delta !== undefined ? ` · delta ${delta >= 0 ? '+' : ''}${delta.toFixed(2)}` : ''}`}
                            >
                              {isEditing ? (
                                <input
                                  autoFocus
                                  type="number"
                                  value={draftValue}
                                  onChange={e => setDraftValue(e.target.value)}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter' && cell.line_id) saveCell(cell.line_id);
                                    else if (e.key === 'Escape') cancelCell();
                                  }}
                                  onBlur={() => cell.line_id && saveCell(cell.line_id)}
                                  title={`Edit ${r.gl_account} @ ${p}`}
                                  style={{
                                    width: 80, padding: '2px 6px', fontSize: 12,
                                    borderRadius: 2, border: '1px solid #fbbf24',
                                    background: '#fffbeb', textAlign: 'right',
                                  }}
                                />
                              ) : (
                                <>
                                  <span>{formatAmount(cell.amount)}</span>
                                  {/* Only show delta arrows when the snapshot
                                      diff is actually per-line (not the
                                      per-metric stub). Today _load_snapshot_amounts
                                      returns an empty lookup so every cell would
                                      show a misleading ▲. Suppress until per-line
                                      snapshots ship. */}
                                  {delta !== undefined && delta !== 0 && !grid.compare?.note && (
                                    <span style={{
                                      marginLeft: 4, fontSize: 10,
                                      color: delta >= 0 ? '#15803d' : '#b91c1c',
                                    }}>
                                      {delta >= 0 ? '▲' : '▼'}
                                    </span>
                                  )}
                                </>
                              )}
                            </td>
                          );
                        })}
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>
                          {formatAmount(r.row_total)}
                        </td>
                      </tr>
                    ))}
                    {grid.band_totals[band] && (
                      <tr style={{ background: '#fafbfc', fontWeight: 600 }}>
                        <td style={{ ...tdStyle, position: 'sticky', left: 0, background: '#fafbfc', fontSize: 11, color: 'var(--text-hint)' }}>
                          {band} subtotal
                        </td>
                        {periods.map(p => (
                          <td key={p} style={{ ...tdStyle, textAlign: 'right', fontSize: 11 }}>
                            {formatAmount(grid.band_totals[band][p] || 0)}
                          </td>
                        ))}
                        <td style={{ ...tdStyle, textAlign: 'right', fontSize: 11 }}>
                          {formatAmount(
                            Object.values(grid.band_totals[band] || {}).reduce((a, b) => a + b, 0),
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {grid.period_totals && (
                  <tr style={{ background: 'var(--navy-900, #1e3a5f)', color: '#fff', fontWeight: 700 }}>
                    <td style={{ ...tdStyle, position: 'sticky', left: 0, background: 'var(--navy-900, #1e3a5f)', color: '#fff' }}>
                      Grand total
                    </td>
                    {periods.map(p => (
                      <td key={p} style={{ ...tdStyle, textAlign: 'right' }}>
                        {formatAmount(grid.period_totals[p] || 0)}
                      </td>
                    ))}
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      {formatAmount(Object.values(grid.period_totals).reduce((a, b) => a + b, 0))}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
}

// ── AssumptionsPanel (scenario.assumptions JSONB editor) ────────────

function AssumptionsPanel({
  scenarioId, scenarioStatus, onComputed, toast,
}: {
  scenarioId: number;
  scenarioStatus: string;
  onComputed: () => void;
  toast: (msg: string, ok?: boolean) => void;
}) {
  const [requiredKeys, setRequiredKeys] = useState<string[]>([]);
  const [keyCategory, setKeyCategory] = useState<Record<string, string>>({});
  const [assumptions, setAssumptions] = useState<Record<string, any>>({});
  const [dirty, setDirty] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const editable = !['APPROVED', 'ACTIVE', 'ARCHIVED'].includes(
    (scenarioStatus || 'DRAFT').toUpperCase(),
  );

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [fRes, sRes] = await Promise.all([
        authFetch(`${API}/formulas`),
        authFetch(`${API}/scenarios/${scenarioId}`),
      ]);
      if (!fRes.ok) {
        const body = await fRes.text().catch(() => '');
        const msg = `/formulas HTTP ${fRes.status} ${body.slice(0, 200)}`;
        console.error('AssumptionsPanel /formulas failed', msg);
        setLoadError(msg);
      } else {
        const d = await fRes.json();
        const required: string[] = Array.isArray(d?.required_assumptions) ? d.required_assumptions : [];
        if (required.length === 0) {
          const msg = `/formulas returned 0 required_assumptions (response keys: ${Object.keys(d || {}).join(', ')})`;
          console.warn('AssumptionsPanel:', msg, d);
          setLoadError(msg);
        }
        setRequiredKeys(required);
        const map: Record<string, string> = {};
        const formulas = Array.isArray(d?.formulas) ? d.formulas : [];
        formulas.forEach((f: any) => {
          (f?.inputs || []).forEach((inp: string) => {
            if (required.includes(inp) && !map[inp]) {
              map[inp] = f.category || 'OTHER';
            }
          });
        });
        setKeyCategory(map);
      }
      if (!sRes.ok) {
        console.error('AssumptionsPanel /scenarios/{id} failed', sRes.status);
      } else {
        const d = await sRes.json();
        setAssumptions(d?.assumptions || {});
      }
      setDirty({});
    } catch (e: any) {
      console.error('AssumptionsPanel load threw', e);
      setLoadError(`load threw: ${e?.message || e}`);
    } finally {
      setLoading(false);
    }
  }, [scenarioId]);

  useEffect(() => { load(); }, [load]);

  const [customKey, setCustomKey] = useState('');

  // Display set = required keys from /formulas (registry) UNION any
  // keys already on the scenario that aren't in the registry (custom
  // user keys, or keys whose registry call failed).
  const displayKeys = useMemo(() => {
    const set = new Set<string>([...requiredKeys, ...Object.keys(assumptions || {})]);
    return Array.from(set);
  }, [requiredKeys, assumptions]);

  const grouped = useMemo(() => {
    const out: Record<string, string[]> = {};
    displayKeys.forEach(k => {
      const cat = keyCategory[k] || (requiredKeys.includes(k) ? 'OTHER' : 'CUSTOM');
      if (!out[cat]) out[cat] = [];
      out[cat].push(k);
    });
    Object.keys(out).forEach(c => out[c].sort());
    return out;
  }, [displayKeys, keyCategory, requiredKeys]);

  const addCustomKey = () => {
    const k = customKey.trim();
    if (!k) return;
    // Just put it in dirty with empty value; the user types the value next.
    setDirty(prev => ({ ...prev, [k]: prev[k] ?? '' }));
    // Also seed an empty in assumptions so the row renders immediately.
    setAssumptions(prev => prev[k] === undefined ? { ...prev, [k]: '' } : prev);
    setCustomKey('');
  };

  const dirtyCount = Object.keys(dirty).length;

  const handleEdit = (key: string, raw: string) => {
    setDirty(prev => ({ ...prev, [key]: raw }));
  };

  const saveAndRecompute = async () => {
    if (dirtyCount === 0) {
      // Just recompute
      await recompute();
      return;
    }
    setSaving(true);
    try {
      // Build PATCH body: numeric parse where possible, null to delete
      const body: Record<string, any> = {};
      for (const [k, raw] of Object.entries(dirty)) {
        if (raw === '' || raw == null) {
          body[k] = null;
          continue;
        }
        const num = Number(raw);
        body[k] = Number.isFinite(num) ? num : raw;
      }
      const pRes = await authFetch(`${API}/scenarios/${scenarioId}/assumptions`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const pData = await pRes.json();
      if (!pRes.ok || !pData.ok) {
        toast(`Save failed: ${pData.detail || 'error'}`, false);
        setSaving(false);
        return;
      }
      // Recompute right after the save lands
      const cRes = await authFetch(`${API}/scenarios/${scenarioId}/compute`, {
        method: 'POST',
      });
      if (cRes.ok) {
        toast(`Saved ${dirtyCount} assumption${dirtyCount === 1 ? '' : 's'} + recomputed`);
        onComputed();
        load();
      } else {
        toast('Saved but recompute failed — retry from the detail panel', false);
        load();
      }
    } catch (e: any) {
      toast(`Save error: ${e.message}`, false);
    } finally {
      setSaving(false);
    }
  };

  const recompute = async () => {
    setSaving(true);
    try {
      const cRes = await authFetch(`${API}/scenarios/${scenarioId}/compute`, {
        method: 'POST',
      });
      if (cRes.ok) {
        toast('Recomputed');
        onComputed();
      } else {
        const d = await cRes.json().catch(() => ({}));
        toast(`Recompute failed: ${d.detail || cRes.status}`, false);
      }
    } finally {
      setSaving(false);
    }
  };

  const totalKeys = requiredKeys.length;
  const filledKeys = requiredKeys.filter(k => assumptions[k] != null && assumptions[k] !== '').length;

  return (
    <div style={PANEL}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div>
          <div style={SECTION_TITLE}>Assumptions</div>
          <div style={{ fontSize: 11, color: 'var(--text-hint)' }}>
            {filledKeys} / {totalKeys} filled · driver graph uses 0 for blanks
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {dirtyCount > 0 && (
            <span style={{
              fontSize: 11, fontWeight: 600, padding: '4px 8px', borderRadius: 4,
              background: '#fef3c7', color: '#a16207', border: '1px solid #fbbf24',
            }}>
              {dirtyCount} unsaved
            </span>
          )}
          <SmallBtn onClick={load} disabled={saving || loading}>Reload</SmallBtn>
          <SmallBtn primary onClick={saveAndRecompute} disabled={!editable || saving}>
            {saving ? 'Working…' : dirtyCount > 0 ? 'Save & Recompute' : 'Recompute'}
          </SmallBtn>
        </div>
      </div>

      {!editable && (
        <div style={{
          fontSize: 12, padding: 8, marginBottom: 10,
          background: '#fef3c7', borderRadius: 4, border: '1px solid #fbbf24',
          color: '#92400e',
        }}>
          Scenario is {scenarioStatus}. Reject + re-propose to edit assumptions.
        </div>
      )}

      {loadError && (
        <div style={{
          fontSize: 12, padding: 8, marginBottom: 10,
          background: '#fee2e2', borderRadius: 4, border: '1px solid #fca5a5',
          color: '#991b1b', fontFamily: 'monospace',
        }}>
          {loadError}
        </div>
      )}

      {loading ? (
        <div style={{ fontSize: 12, color: 'var(--text-hint)' }}>Loading driver registry + assumptions…</div>
      ) : displayKeys.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text-hint)' }}>
          No assumptions yet. Add one below — any key the driver graph
          knows about (e.g. <code>revenue_growth_rate</code>, <code>gross_margin_pct</code>,
          <code>hires_period</code>, <code>monthly_burn</code>) becomes usable when
          you Save & Recompute.
        </div>
      ) : (
        Object.entries(grouped).map(([cat, keys]) => (
          <div key={cat} style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: cat === 'CUSTOM' ? '#0e7490' : '#7c3aed', textTransform: 'uppercase', letterSpacing: 0.04, marginBottom: 6 }}>
              {cat}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {keys.map(k => {
                  const current = assumptions[k];
                  const draft = dirty[k];
                  const displayValue = draft !== undefined ? draft : (current == null ? '' : String(current));
                  const isDirty = draft !== undefined;
                  return (
                    <tr key={k}>
                      <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 12, width: '50%', color: 'var(--navy-900)' }}>
                        {k}
                      </td>
                      <td style={tdStyle}>
                        <input
                          type="text"
                          value={displayValue}
                          onChange={e => handleEdit(k, e.target.value)}
                          disabled={!editable}
                          placeholder="—"
                          title={`Edit ${k}`}
                          style={{
                            width: '100%', padding: '4px 8px', fontSize: 12,
                            borderRadius: 4,
                            border: `1px solid ${isDirty ? '#fbbf24' : 'var(--border-default, #cbd5e1)'}`,
                            background: isDirty ? '#fffbeb' : '#fff',
                            color: 'var(--navy-900)',
                          }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))
      )}

      {editable && (
        <div style={{
          marginTop: 16, paddingTop: 12, borderTop: '1px dashed var(--border-subtle)',
          display: 'flex', gap: 6, alignItems: 'center',
        }}>
          <span style={{ fontSize: 11, color: 'var(--text-hint)', fontWeight: 600 }}>+ Add assumption:</span>
          <input
            type="text"
            value={customKey}
            onChange={e => setCustomKey(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomKey(); } }}
            placeholder="assumption_key_name"
            title="New assumption key"
            style={{
              flex: 1, padding: '4px 8px', fontSize: 12,
              borderRadius: 4, border: '1px solid var(--border-default)',
              fontFamily: 'monospace',
            }}
          />
          <SmallBtn onClick={addCustomKey} disabled={!customKey.trim()}>Add row</SmallBtn>
        </div>
      )}
    </div>
  );
}

// ── NewScenarioModal (POST /scenarios) ─────────────────────────────

function NewScenarioModal({
  onClose, onCreated, toast,
}: {
  onClose: () => void;
  onCreated: (newId: number) => void;
  toast: (msg: string, ok?: boolean) => void;
}) {
  const [name, setName] = useState('');
  const [scenarioType, setScenarioType] = useState('CUSTOM');
  const [fiscalYear, setFiscalYear] = useState<number>(new Date().getFullYear());
  const [entityCodes, setEntityCodes] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim()) {
      toast('Name is required', false);
      return;
    }
    setSaving(true);
    try {
      const ec = entityCodes.trim()
        ? entityCodes.split(',').map(s => s.trim()).filter(Boolean)
        : null;
      const res = await authFetch(`${API}/scenarios`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          scenario_type: scenarioType,
          fiscal_year: Number(fiscalYear),
          description: description.trim() || null,
          entity_codes: ec,
          assumptions: {},
          actor: 'OPERATOR',
        }),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        toast(`Created scenario #${data.id}`);
        onCreated(data.id);
      } else {
        toast(`Create failed: ${data.detail || 'error'}`, false);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 8, padding: 24,
          width: 480, boxShadow: '0 24px 48px rgba(0,0,0,0.25)',
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy-900)', marginBottom: 4 }}>
          New scenario
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-hint)', marginBottom: 16 }}>
          Creates a DRAFT scenario. Fill in assumptions afterward and recompute.
        </div>

        <FormRow label="Name" required>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Q2 stretch plan"
            style={inputStyle}
          />
        </FormRow>

        <FormRow label="Type">
          <select
            title="Scenario type"
            value={scenarioType}
            onChange={e => setScenarioType(e.target.value)}
            style={inputStyle}
          >
            <option value="BASE">BASE</option>
            <option value="CUSTOM">CUSTOM</option>
            <option value="UPSIDE">UPSIDE</option>
            <option value="DOWNSIDE">DOWNSIDE</option>
            <option value="PLAUSIBLE_BASE">PLAUSIBLE_BASE</option>
          </select>
        </FormRow>

        <FormRow label="Fiscal year">
          <input
            type="number"
            title="Fiscal year"
            placeholder="2026"
            value={fiscalYear}
            onChange={e => setFiscalYear(Number(e.target.value) || 0)}
            style={inputStyle}
          />
        </FormRow>

        <FormRow label="Entity codes" hint="Comma-separated. Blank = all entities.">
          <input
            type="text"
            value={entityCodes}
            onChange={e => setEntityCodes(e.target.value)}
            placeholder="1000, 3000"
            style={inputStyle}
          />
        </FormRow>

        <FormRow label="Description" hint="Optional">
          <textarea
            title="Scenario description"
            placeholder="Optional notes about the scenario"
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={2}
            style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
          />
        </FormRow>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <SmallBtn onClick={onClose}>Cancel</SmallBtn>
          <SmallBtn primary onClick={submit} disabled={saving || !name.trim()}>
            {saving ? 'Creating…' : 'Create'}
          </SmallBtn>
        </div>
      </div>
    </div>
  );
}

function FormRow({
  label, hint, required, children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-hint)', marginBottom: 4, display: 'flex', justifyContent: 'space-between' }}>
        <span>{label}{required && <span style={{ color: '#b91c1c', marginLeft: 4 }}>*</span>}</span>
        {hint && <span style={{ fontWeight: 400, fontStyle: 'italic' }}>{hint}</span>}
      </div>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '6px 10px',
  fontSize: 13,
  borderRadius: 4,
  border: '1px solid var(--border-default, #cbd5e1)',
  color: 'var(--navy-900)',
  background: '#fff',
  boxSizing: 'border-box',
};

// ── OverviewBar (no-click landing, real data only) ─────────────────

function OverviewBar({
  scenarios, loading,
}: {
  scenarios: Scenario[];
  loading: boolean;
}) {
  const [recentEdits, setRecentEdits] = useState<AuditEdit[]>([]);
  const [stagedDraftCount, setStagedDraftCount] = useState<number>(0);
  const [activityLoaded, setActivityLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [eRes, dRes] = await Promise.all([
          authFetch(`${API}/audit-log?limit=5`),
          authFetch(`${API}/drafts?status=STAGED&limit=200`),
        ]);
        if (eRes.ok) {
          const d = await eRes.json();
          setRecentEdits(Array.isArray(d) ? d : []);
        }
        if (dRes.ok) {
          const d = await dRes.json();
          setStagedDraftCount(Array.isArray(d.drafts) ? d.drafts.length : 0);
        }
      } catch (e) {
        console.error('OverviewBar load', e);
      } finally {
        setActivityLoaded(true);
      }
    })();
  }, []);

  const activeCount = scenarios.filter(s => s.is_active).length;
  const computedCount = scenarios.filter(s => s.has_computed_outputs).length;
  // "Recently computed" — anything where the scenario was created/updated
  // in the last 24h AND has computed_outputs. Honest empty otherwise.
  const recentEditCount = recentEdits.length;

  return (
    <div style={{
      background: 'var(--surface-default, #fff)',
      border: '1px solid var(--border-subtle)',
      borderRadius: 6,
      padding: 16,
      marginBottom: 12,
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr) 1.5fr',
      gap: 16,
    }}>
      <KpiTile
        label="Scenarios"
        value={loading ? '…' : `${activeCount} active`}
        sub={loading ? '' : `${scenarios.length} total`}
      />
      <KpiTile
        label="Computed"
        value={loading ? '…' : `${computedCount} / ${scenarios.length || 0}`}
        sub={computedCount === 0 && !loading ? 'none computed yet' : 'has computed_outputs'}
      />
      <KpiTile
        label="Recent edits"
        value={activityLoaded ? String(recentEditCount) : '…'}
        sub={activityLoaded && recentEditCount === 0 ? 'no audit-log entries' : 'last 5'}
      />
      <KpiTile
        label="Staged drafts"
        value={activityLoaded ? String(stagedDraftCount) : '…'}
        sub={
          activityLoaded && stagedDraftCount === 0
            ? 'nothing pending'
            : 'awaiting promote'
        }
      />

      <div style={{ borderLeft: '1px solid var(--border-subtle)', paddingLeft: 16 }}>
        <div style={{ fontSize: 11, color: 'var(--text-hint)', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.04 }}>
          Latest activity
        </div>
        {!activityLoaded && (
          <div style={{ fontSize: 12, color: 'var(--text-hint)' }}>Loading…</div>
        )}
        {activityLoaded && recentEdits.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--text-hint)' }}>
            No edits yet. Make a change to a scenario or stage edits from the Ask tab.
          </div>
        )}
        {activityLoaded && recentEdits.length > 0 && (
          <div>
            {recentEdits.slice(0, 3).map(e => (
              <div key={e.id} style={{ fontSize: 12, padding: '3px 0', display: 'flex', gap: 6, alignItems: 'center' }}>
                <SourceBadge source={e.source} />
                <span style={{ color: 'var(--text-secondary, #475569)' }}>
                  {e.actor || 'system'} {e.action.toLowerCase()} {e.field_name ? <code style={{ fontSize: 11 }}>{e.field_name}</code> : null}
                </span>
                <span style={{ color: 'var(--text-hint)', marginLeft: 'auto', fontSize: 11 }}>
                  {shortDate(e.created_at)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function KpiTile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div>
      <div style={{
        fontSize: 11, fontWeight: 600, color: 'var(--text-hint)',
        textTransform: 'uppercase', letterSpacing: 0.04, marginBottom: 4,
      }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--navy-900)', lineHeight: 1.15 }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 11, color: 'var(--text-hint)', marginTop: 2 }}>{sub}</div>
      )}
    </div>
  );
}

// ── WhatNowPanel (real-state action cards, no demo data) ──────────

interface ActionCard {
  key: string;
  title: string;
  detail: string;
  cta: string;
  onClick: () => void;
}

function WhatNowPanel({
  scenarios, reload, toast, goToTab,
}: {
  scenarios: Scenario[];
  reload: () => void;
  toast: (msg: string, ok?: boolean) => void;
  goToTab: (key: string) => void;
}) {
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [stagedBatches, setStagedBatches] = useState<number>(0);

  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(`${API}/drafts?status=STAGED&limit=200`);
        if (res.ok) {
          const d = await res.json();
          const drafts = Array.isArray(d.drafts) ? d.drafts : [];
          const batches = new Set(drafts.map((r: any) => r.batch_id));
          setStagedBatches(batches.size);
        }
      } catch (e) {
        console.error('WhatNowPanel staged load', e);
      }
    })();
  }, [scenarios]);

  // Build real-state action cards. Each card MUST correspond to a real
  // state condition; no aspirational suggestions.
  const cards = useMemo<ActionCard[]>(() => {
    const out: ActionCard[] = [];

    // 1) Scenarios in PROPOSED state -> Approve / Reject
    const proposed = scenarios.filter(s => (s.status || '').toUpperCase() === 'PROPOSED');
    proposed.slice(0, 2).forEach(s => {
      out.push({
        key: `approve-${s.id}`,
        title: `Approve ${s.name}`,
        detail: `Currently PROPOSED. Approving snapshots assumptions + computed_outputs and stamps the actor + timestamp.`,
        cta: 'Approve',
        onClick: async () => {
          setBusyKey(`approve-${s.id}`);
          try {
            const r = await authFetch(`${API}/scenarios/${s.id}/approve`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ actor: 'OPERATOR' }),
            });
            const d = await r.json();
            if (r.ok && d.ok) {
              toast(`Approved ${s.name}: ${d.from} → ${d.to}`);
              reload();
            } else {
              toast(`Approve failed: ${d.detail || d.reason || 'error'}`, false);
            }
          } finally {
            setBusyKey(null);
          }
        },
      });
    });

    // 2) DRAFT scenarios with no computed_outputs -> Compute
    const draftUncomputed = scenarios.filter(
      s => !s.has_computed_outputs && (s.status || 'DRAFT').toUpperCase() === 'DRAFT',
    );
    draftUncomputed.slice(0, 2).forEach(s => {
      out.push({
        key: `compute-${s.id}`,
        title: `Compute ${s.name}`,
        detail: `DRAFT scenario with no computed outputs yet. Runs the driver graph across the fiscal year and caches results.`,
        cta: 'Compute',
        onClick: async () => {
          setBusyKey(`compute-${s.id}`);
          try {
            const r = await authFetch(`${API}/scenarios/${s.id}/compute`, { method: 'POST' });
            if (r.ok) {
              toast(`Computed ${s.name}`);
              reload();
            } else {
              const d = await r.json().catch(() => ({}));
              toast(`Compute failed: ${d.detail || r.status}`, false);
            }
          } finally {
            setBusyKey(null);
          }
        },
      });
    });

    // 3) Staged drafts pending review
    if (stagedBatches > 0) {
      out.push({
        key: 'review-drafts',
        title: `Review ${stagedBatches} staged draft batch${stagedBatches === 1 ? '' : 'es'}`,
        detail: 'Claude or HUMAN-staged plan edits are awaiting promote/discard. Each batch is atomic.',
        cta: 'Open Drafts',
        onClick: () => goToTab('drafts'),
      });
    }

    // 4) When there are scenarios but no actions surfaced, prompt the
    //    most engaging next step: Ask the Plan. ONLY surface if other
    //    cards exist or scenarios exist; no fabricated "try Ask" filler.
    if (out.length === 0 && scenarios.length > 0) {
      out.push({
        key: 'ask-the-plan',
        title: 'Ask the plan',
        detail: 'No state-driven actions pending. Ask a question grounded in your scenarios, drivers, BvA, or runway.',
        cta: 'Open Ask',
        onClick: () => goToTab('ask'),
      });
    }

    return out.slice(0, 4);
  }, [scenarios, stagedBatches, reload, toast, goToTab]);

  // No scenarios at all = honest empty state pointing at how to create one
  if (scenarios.length === 0) {
    return (
      <div style={{
        background: 'var(--surface-default, #fff)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 6, padding: 16, marginBottom: 12,
      }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy-900)', marginBottom: 6 }}>
          No scenarios yet
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary, #475569)', lineHeight: 1.55 }}>
          Create a scenario via the backend API (<code>POST /api/v1/budgets</code> with a
          fiscal_year + period_type creates a BudgetHeader that the planning engine can compute
          against). Once a scenario exists here, this panel will surface state-driven next
          actions.
        </div>
      </div>
    );
  }

  if (cards.length === 0) {
    return (
      <div style={{
        background: 'var(--surface-default, #fff)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 6, padding: 12, marginBottom: 12,
        fontSize: 12, color: 'var(--text-hint)',
      }}>
        Nothing pending. All scenarios are computed, no drafts staged, no proposed/draft state.
      </div>
    );
  }

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${Math.min(cards.length, 4)}, 1fr)`,
      gap: 12,
      marginBottom: 12,
    }}>
      {cards.map(c => (
        <div key={c.key} style={{
          background: 'var(--surface-default, #fff)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 6,
          padding: 14,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: 0.04 }}>
            What now?
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy-900)', lineHeight: 1.25 }}>
            {c.title}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary, #475569)', lineHeight: 1.45, flex: 1 }}>
            {c.detail}
          </div>
          <button
            type="button"
            onClick={c.onClick}
            disabled={busyKey === c.key}
            style={{
              padding: '6px 12px',
              fontSize: 12,
              fontWeight: 600,
              borderRadius: 4,
              cursor: busyKey === c.key ? 'not-allowed' : 'pointer',
              border: 'none',
              background: 'var(--navy-900, #1e3a5f)',
              color: '#fff',
              alignSelf: 'flex-start',
              opacity: busyKey === c.key ? 0.6 : 1,
            }}
          >
            {busyKey === c.key ? 'Working…' : c.cta}
          </button>
        </div>
      ))}
    </div>
  );
}
